<template>
  <div class="input">
    <label
      v-if="!internalLabel || !modelValue"
      class="input__label sr-only"
      :for="id">
      {{ label }}
    </label>
    <div class="input__container">
      <label
        v-if="internalLabel && modelValue"
        class="input__internal-label"
        :for="id">
        {{ placeholder }}
      </label>
      <input
        :id="id"
        type="text"
        autocomplete="off"
        :value="modelValue"
        @input="updateValue"
        @focus="handleFocus"
        @click="showSuggestions = true"
        @blur="handleBlur"
        @keydown="handleKeydown"
        :placeholder="placeholder"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isListOpen"
        :aria-controls="listboxId"
        :aria-activedescendant="
          isListOpen && selectedIndex >= 0 ? optionId(selectedIndex) : undefined
        "
        :class="[
          'input__field',
          { 'input__field--internal-label': internalLabel && modelValue },
        ]" />
      <svg
        v-if="icon"
        class="input__icon">
        <use :xlink:href="`#${icon}`" />
      </svg>
      <button
        v-if="hasDelete && modelValue"
        class="input__delete-button"
        :aria-label="$t('common.actions.clear')"
        @click="clearInput"
        type="button">
        &times;
      </button>
    </div>
    <!-- Focus stays in the input; the arrow keys move the highlight. -->
    <ul
      v-if="isListOpen"
      :id="listboxId"
      role="listbox"
      :aria-label="label"
      class="input__suggestions-list"
      @mousedown.prevent>
      <li
        v-for="(suggestion, index) in suggestions"
        :id="optionId(index)"
        :key="suggestion.key ?? suggestion.code"
        role="option"
        :aria-selected="index === selectedIndex"
        @mousedown.prevent="selectSuggestion(suggestion)"
        :class="{
          'input__suggestion--highlighted': index === selectedIndex,
          'input__suggestion--child': suggestion.isChild,
        }"
        class="input__suggestion">
        <div class="input__suggestion-name">{{ suggestion.name }}</div>
        <!-- A group's stops share its place, which is already shown on the group's row. -->
        <div
          v-if="!suggestion.isChild && (suggestion.district || suggestion.county)"
          class="input__suggestion-location">
          <span v-if="suggestion.district">{{ suggestion.district }}</span>
          <span v-if="suggestion.district && suggestion.county">, </span>
          <span v-if="suggestion.county">{{ suggestion.county }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, watch } from 'vue'

  type Suggestion = {
    // Unique per row; `code` alone may repeat (e.g. every stop of an agglomeration).
    key?: string
    name: string
    code: string
    district?: string
    county?: string
    isChild?: boolean
  }

  export default defineComponent({
    name: 'Input',
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        required: true,
      },
      modelValue: {
        type: String,
        required: true,
      },
      placeholder: {
        type: String,
        default: '',
      },
      // Shown as given: the caller does the filtering.
      suggestions: {
        type: Array as () => Suggestion[],
        default: () => [],
      },
      icon: {
        type: String,
        default: '',
      },
      hasDelete: {
        type: Boolean,
        default: false,
      },
      internalLabel: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'focus', 'select-suggestion'],
    setup(props, { emit }) {
      const showSuggestions = ref(false)
      const selectedIndex = ref(-1)

      const isListOpen = computed(() => showSuggestions.value && props.suggestions.length > 0)
      const listboxId = computed(() => `${props.id}-suggestions`)
      const optionId = (index: number) => `${props.id}-suggestion-${index}`

      const updateValue = (event: Event) => {
        const target = event.target as HTMLInputElement
        emit('update:modelValue', target.value)
        showSuggestions.value = true
      }

      const handleFocus = (event: Event) => {
        showSuggestions.value = true
        selectedIndex.value = -1
        emit('focus', event)
      }

      const handleBlur = () => {
        showSuggestions.value = false
        selectedIndex.value = -1
      }

      const selectSuggestion = (suggestion: Suggestion) => {
        emit('update:modelValue', suggestion.name)
        emit('select-suggestion', suggestion.code)
        showSuggestions.value = false
      }

      // The list changes as the user types, so a highlighted row may no longer exist.
      watch(
        () => props.suggestions,
        () => {
          selectedIndex.value = -1
        },
      )

      const handleKeydown = (event: KeyboardEvent) => {
        const count = props.suggestions.length
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault()
          if (!count) return
          showSuggestions.value = true
        }
        if (event.key === 'ArrowDown') {
          selectedIndex.value = (selectedIndex.value + 1) % count
        } else if (event.key === 'ArrowUp') {
          selectedIndex.value = (selectedIndex.value - 1 + count) % count
        } else if (
          event.key === 'Enter' &&
          isListOpen.value &&
          props.suggestions[selectedIndex.value]
        ) {
          event.preventDefault()
          selectSuggestion(props.suggestions[selectedIndex.value])
        } else if (event.key === 'Escape') {
          showSuggestions.value = false
        }
      }

      const clearInput = () => {
        emit('update:modelValue', '')
      }

      return {
        showSuggestions,
        selectedIndex,
        isListOpen,
        listboxId,
        optionId,
        updateValue,
        handleFocus,
        handleBlur,
        selectSuggestion,
        handleKeydown,
        clearInput,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .input {
    display: flex;
    flex-direction: column;
    position: relative;

    &__label {
      margin-bottom: rem(8);
      font-weight: bold;
    }

    &__internal-label {
      @include body-small;
      position: absolute;
      top: rem(10);
      left: 16px;
      color: $clr-heading;

      &:has(+ .input__field + .input__icon) {
        left: 40px;
      }
    }

    &__container {
      display: flex;
      align-items: center;
      position: relative;
      min-height: rem(64);
    }

    &__icon {
      position: absolute;
      left: 16px;
      width: 16px;
      height: 16px;
      fill: currentColor;
      color: $clr-secondary;
      pointer-events: none;
    }

    &__field {
      @include body-large;
      padding: rem(19) 16px;
      color: $clr-text;
      border: 1px solid $clr-primary-50;
      border-radius: 8px;
      background-color: $clr-background;
      flex: 1;
      box-shadow: $box-shadow-card;

      &:has(+ .input__icon) {
        min-height: 23.62px;
        padding-left: 40px;
      }

      &:active,
      &:focus-visible,
      &:focus {
        border-color: $clr-primary-700;
      }

      &:disabled {
        background-color: #f5f5f5;
        box-shadow: none;
      }

      @media (min-width: $breakpoint-xl) {
        width: 100%;

        &:has(+ .input__icon) {
          min-width: 220px;
        }
      }
    }

    &__field--internal-label {
      padding-bottom: 0.65rem;
      padding-top: 1.75rem;
    }

    &__delete-button {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: $clr-primary-300;
      cursor: pointer;
      position: absolute;
      right: 1rem;
    }

    &__suggestions-list {
      top: 55px;
      list-style: none;
      padding: 0;
      text-align: left;
      margin: 0;
      border: 1px solid $clr-primary-100;
      border-radius: 8px;
      max-height: 440px;
      overflow-y: auto;
      color: $clr-text;
      background-color: $clr-white;
      position: absolute;
      width: 100%;
      z-index: 1000;
    }

    &__suggestion {
      padding: 0.5rem;
      cursor: pointer;

      &:hover,
      &--highlighted {
        background-color: $clr-primary-50;
        box-shadow: $box-shadow-hover;
      }

      &--child {
        padding-left: rem(24);
      }

      &-name {
        font-weight: 500;
      }

      &-location {
        @include body-small;
        color: $clr-text-disable;
        margin-top: rem(2);
      }
    }
  }
</style>
